<script setup>
import { computed, inject } from 'vue';
import { ctaButtons, ctaHasContent } from '../blockContent.js';
import { heroFocusPosition } from '../heroImage.js';
import { accentParts, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// The design has one CTA band and it sits on the accent colour. With
// an image it becomes the mockup's "Membership + volunteer CTA" split
// (photo one side, words the other); without one the same band runs
// centered, which is the shape Classic has always drawn.
const split = computed(() => !!props.config.image);

const headingParts = computed(() => accentParts(props.config.heading));
const headingTag = computed(() => (['h1', 'h2', 'h3', 'h4'].includes(props.config.heading_level) ? props.config.heading_level : 'h2'));
const headingStyle = computed(() => props.config.heading_style || 'display');
// display → the band's own big Archivo heading; regular → the smaller
// one; eyebrow → the mono caps line, which is already a type style
// here rather than a heading size.
const headingClass = computed(() => ({
    display: 'th-h2 th-cta__heading',
    regular: 'th-h2 th-h2--sm th-cta__heading',
    eyebrow: 'th-eyebrow th-cta__heading--eyebrow',
}[headingStyle.value] || 'th-h2 th-cta__heading'));

const widthClass = computed(() => `th-cta__inner--${props.config.content_width || 'medium'}`);
// Nothing to say is no block for a visitor (ctaHasContent); the editor
// keeps the empty band so there is something to click (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => ctaHasContent(props.config));
const buttons = computed(() => (editing ? (Array.isArray(props.config.buttons) ? props.config.buttons : []) : ctaButtons(props.config)));
</script>

<template>
    <section v-if="hasContent || editing" :id="config.anchor_id || null"
             class="th-block th-cta th-on-accent"
             :class="split ? 'th-cta--split' : 'th-cta--center'"
             :style="styleVars(config)">
        <div class="th-wrap th-cta__band" :class="{ 'th-split': split }">
            <div v-if="split" class="th-media th-media--on-accent th-cta__photo">
                <img :src="config.image" alt="" :style="{ objectPosition: heroFocusPosition(config) }">
            </div>

            <div class="th-cta__inner" :class="split ? null : widthClass">
                <p v-if="config.eyebrow" class="th-eyebrow th-cta__eyebrow">{{ config.eyebrow }}</p>
                <component :is="headingTag" v-if="config.heading" :class="headingClass">
                    <template v-for="(part, i) in headingParts" :key="i">
                        <span v-if="part.accent" class="th-cta__accent">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                </component>
                <p v-if="config.subtitle" class="th-body th-cta__sub">{{ config.subtitle }}</p>

                <div v-if="buttons.length" class="th-row th-cta__buttons">
                    <a v-for="(b, i) in buttons" :key="i"
                       :href="b.href || '#'"
                       class="th-btn th-cta__btn"
                       :class="[
                           (b.style || 'primary') === 'ghost' ? 'th-btn--ghost-light' : 'th-cta__btn--primary',
                           { 'th-cta__btn--hide-mobile': b.hide_on_mobile },
                       ]">
                        {{ b.label }}
                    </a>
                </div>

                <p v-if="config.note" class="th-small th-cta__note">{{ config.note }}</p>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-cta__band { padding-top: clamp(48px, 7vw, 96px); padding-bottom: clamp(48px, 7vw, 96px); }
.th-cta--center .th-cta__inner { margin: 0 auto; text-align: center; }
.th-cta__inner--narrow { max-width: 600px; }
.th-cta__inner--medium { max-width: 800px; }
.th-cta__inner--wide { max-width: 1000px; }
.th-cta__inner--full { max-width: none; }

.th-cta__photo { aspect-ratio: 5 / 4; }
.th-cta__eyebrow { color: var(--text-eyebrow, currentColor); }
.th-cta__heading { margin-bottom: 18px; }
.th-cta__heading--eyebrow { margin-bottom: 18px; color: var(--text-heading, currentColor); }
/* The accent colour IS the band, so an accent word reads as a softer
   white rather than disappearing into the background. */
.th-cta__accent { opacity: .68; }
.th-cta__sub { font-size: 18px; margin-bottom: 28px; max-width: 46ch; }
.th-cta--center .th-cta__sub { margin-left: auto; margin-right: auto; }
.th-cta--center .th-cta__buttons { justify-content: center; }
.th-cta__btn--primary { background: var(--btn-bg, #fff); color: var(--btn-text, var(--th-ink)); }
.th-cta__note { margin-top: 20px; color: var(--text-sub, rgba(255, 255, 255, .8)); }

@media (max-width: 640px) {
    .th-cta__btn--hide-mobile { display: none; }
}
</style>
